import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { fetchLectureData } from '../services/googleSheets';
import useMediaQuery from '../hooks/useMediaQuery';
import { useConfig } from '../config/ConfigContext';
import { isCommunityFeatureEnabled, getEnabledCommunityFeatures, getEnabledEmbeddedApps } from '../utils/featureHelpers';

/**
 * MobileHamburgerMenu component for mobile navigation
 * Shows main navigation and contextual options based on current page
 * @param {Object} props - Component props
 * @param {Object} props.currentLecture - Current lecture data (if on a lecture page)
 * @returns {JSX.Element}
 */
const MobileHamburgerMenu = ({ currentLecture }) => {
  const { config, theme, loading: configLoading, error: configError } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [eventLectures, setEventLectures] = useState([]);
  const [eventSpeakers, setEventSpeakers] = useState([]);
  const [speakerLectures, setSpeakerLectures] = useState([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine if we're on a lecture page
  const isLecturePage = useMemo(() => {
    return location.pathname.includes('/lecture/');
  }, [location.pathname]);

  // Memoize the current event name to prevent unnecessary re-renders
  const currentEventName = useMemo(() => {
    return currentLecture?.event || '';
  }, [currentLecture?.event]);

  // Memoize the event filtering function
  const filterLecturesByEvent = useCallback((lectures, eventName) => {
    if (!lectures || !eventName) return [];
    return lectures.filter(lecture => lecture.event === eventName);
  }, []);

  // Memoize the speaker extraction function
  const extractSpeakers = useCallback((lectures) => {
    if (!lectures || !lectures.length) return [];
    return [...new Set(lectures.map(lecture => lecture.speaker))];
  }, []);

  // Load lecture data when on a lecture page and menu is opened
  useEffect(() => {
    // Only load data if we're on a lecture page and the menu is open
    if (!isLecturePage || !isOpen || !currentLecture?.event) return;
    
    // Wait for config
    if (configLoading || !config) {
      console.log('MobileHamburgerMenu: Waiting for config...');
      setLoading(true); // Keep loading state while waiting for config
      return; 
    }
    // Handle config error
    if (configError) {
      console.error('MobileHamburgerMenu: Config loading failed:', configError);
      setError(`Config Error: ${configError.message || configError}`);
      setLoading(false);
      return;
    }
    // Ensure google_sheet_id is available
    if (!config.google_sheet_id) {
      console.error('MobileHamburgerMenu: google_sheet_id is missing from config.');
      setError('Spreadsheet ID is missing in configuration.');
      setLoading(false);
      return;
    }

    // Skip loading if we already have data for this event
    if (eventLectures.length > 0 && 
        eventLectures[0].event === currentLecture.event) {
      return;
    }

    let isMounted = true;
    setLoading(true);

    const loadEventData = async () => {
      try {
        console.log(`MobileHamburgerMenu: Fetching event data for sheet ID: ${config.google_sheet_id}`);
        const allLectures = await fetchLectureData(config.google_sheet_id); // Pass sheetId
        
        if (!isMounted) return;
        
        if (allLectures && allLectures.length > 0) {
          const relevantLectures = filterLecturesByEvent(allLectures, currentEventName);
          
          if (relevantLectures.length > 0) {
            setEventLectures(relevantLectures);
            setEventSpeakers(extractSpeakers(relevantLectures));
          }
        }
      } catch (error) {
        console.error('Error loading event data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadEventData();
    
    return () => {
      isMounted = false;
    };
  }, [isLecturePage, isOpen, currentLecture, currentEventName, filterLecturesByEvent, extractSpeakers, eventLectures, config, configLoading, configError]);

  // Memoized event handlers
  const toggleMenu = useCallback(() => {
    setIsOpen(prevState => {
      if (!prevState) {
        // Reset state when opening
        setActiveSection(null);
        setSelectedSpeaker(null);
      }
      return !prevState;
    });
  }, []);

  const handleSectionClick = useCallback((section) => {
    setActiveSection(prevSection => section === prevSection ? null : section);
    setSelectedSpeaker(null);
  }, []);

  const handleSpeakerClick = useCallback((speaker) => {
    setSelectedSpeaker(prevSpeaker => {
      const newSpeaker = speaker === prevSpeaker ? null : speaker;
      
      if (newSpeaker && newSpeaker !== prevSpeaker) {
        const filteredLectures = eventLectures.filter(lecture => 
          lecture.speaker === newSpeaker
        );
        setSpeakerLectures(filteredLectures);
      }
      
      return newSpeaker;
    });
  }, [eventLectures]);

  const handleLectureClick = useCallback((lectureId) => {
    navigate(`/lecture/${lectureId}`);
    setIsOpen(false);
  }, [navigate]);

  const handleNavigation = useCallback((path) => {
    navigate(path);
    setIsOpen(false);
  }, [navigate]);

  // Use the custom hook for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Memoized styles - ALWAYS define hooks before any conditional returns
  const styles = useMemo(() => ({
    menuButton: {
      position: 'fixed',
      top: '15px',
      right: '15px',
      width: '36px',
      height: '36px',
      borderRadius: '5px',
      backgroundColor: theme.colors.primary,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '20px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
      border: 'none',
      cursor: 'pointer',
      zIndex: 1001,
      opacity: 0.9,
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' : 'hidden',
      transition: 'opacity 0.3s, visibility 0.3s',
    },
    menuContainer: {
      position: 'fixed',
      top: 0,
      left: isOpen ? 0 : '-80%',
      width: '80%',
      height: '100%',
      backgroundColor: 'white',
      boxShadow: '2px 0 10px rgba(0, 0, 0, 0.2)',
      transition: 'left 0.3s ease-in-out',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    menuHeader: {
      padding: '20px',
      borderBottom: '1px solid #eee',
      backgroundColor: theme.colors.primary,
      color: 'white',
    },
    menuContent: {
      flex: 1,
      overflowY: 'auto',
    },
    navItem: {
      padding: '15px 20px',
      borderBottom: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    },
    sectionHeader: {
      padding: '15px 20px',
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
      borderBottom: '1px solid #ddd',
      cursor: 'pointer',
    },
    backButton: {
      padding: '12px 20px',
      borderBottom: '1px solid #eee',
      backgroundColor: '#f9f9f9',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    },
    lectureItem: (isActive) => ({
      padding: '12px 20px',
      borderBottom: '1px solid #eee',
      backgroundColor: isActive ? '#f5f5f5' : 'white',
      cursor: 'pointer',
    }),
  }), [isOpen, theme.colors.primary]);

  // Memoized lecture list
  const lectureList = useMemo(() => {
    if (loading) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
    }
    
    if (eventLectures.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>No lectures found</div>;
    }
    
    return eventLectures.map((lecture, index) => (
      <div 
        key={lecture.lectureId || index} 
        style={styles.lectureItem(lecture.lectureId === currentLecture?.lectureId)}
        onClick={() => handleLectureClick(lecture.lectureId)}
      >
        <div style={{ fontWeight: 'bold' }}>{lecture.title}</div>
        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
          Speaker: {lecture.speaker}
        </div>
      </div>
    ));
  }, [eventLectures, loading, currentLecture?.lectureId, styles, handleLectureClick]);

  // Memoized speakers list
  const speakersList = useMemo(() => {
    if (loading) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
    }
    
    if (eventSpeakers.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>No speakers found</div>;
    }
    
    return eventSpeakers.map((speaker, index) => (
      <div 
        key={index} 
        style={styles.navItem}
        onClick={() => handleSpeakerClick(speaker)}
      >
        {speaker}
      </div>
    ));
  }, [eventSpeakers, loading, styles, handleSpeakerClick]);

  // Memoized speaker lectures list
  const speakerLecturesList = useMemo(() => {
    if (speakerLectures.length === 0) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>No lectures found for this speaker</div>;
    }
    
    return speakerLectures.map((lecture, index) => (
      <div 
        key={lecture.lectureId || index} 
        style={styles.lectureItem(lecture.lectureId === currentLecture?.lectureId)}
        onClick={() => handleLectureClick(lecture.lectureId)}
      >
        <div style={{ fontWeight: 'bold' }}>{lecture.title}</div>
        <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
          Event: {lecture.event}
        </div>
      </div>
    ));
  }, [speakerLectures, currentLecture?.lectureId, styles, handleLectureClick]);

  // Handle config loading/error state before rendering
  if (configLoading) {
    return (
      <button style={styles.menuButton} onClick={toggleMenu}>
        ☰
      </button>
    );
  }

  if (configError) {
    return (
      <div>
        <button style={styles.menuButton} onClick={toggleMenu}>
          ☰
        </button>
        {isOpen && (
          <div style={styles.overlay} onClick={toggleMenu}>
            <div style={styles.menuContent}>
              <p style={{ color: 'red', padding: '20px' }}>Error loading configuration.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Handle data loading state
  if (loading && isOpen && isLecturePage) {
    return (
      <div>
        <button style={styles.menuButton} onClick={toggleMenu}>
          ☰
        </button>
        {isOpen && (
          <div style={styles.overlay} onClick={toggleMenu}>
            <div style={styles.menuContent}>
              <p style={{ padding: '20px' }}>Loading event data...</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Handle error state
  if (error && isOpen) {
    return (
      <div>
        <button style={styles.menuButton} onClick={toggleMenu}>
          ☰
        </button>
        {isOpen && (
          <div style={styles.overlay} onClick={toggleMenu}>
            <div style={styles.menuContent}>
              <p style={{ color: 'red', padding: '20px' }}>{error}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Don't render anything if not on mobile - AFTER all hooks are defined
  if (!isMobile) {
    return null;
  }

  return (
    <>
      {/* Hamburger menu button - always shows ☰ icon */}
      <button
        aria-label="Open menu"
        style={styles.menuButton}
        onClick={toggleMenu}
      >
        ☰
      </button>

      {/* Overlay */}
      <div style={styles.overlay} onClick={toggleMenu}></div>

      {/* Menu container */}
      <div style={styles.menuContainer}>
        <div style={styles.menuHeader}>
          <h2 style={{ margin: 0 }}>Menu</h2>
        </div>

        <div style={styles.menuContent}>
          {/* Main navigation items */}
          <Link to={`/${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
            Home
          </Link>
          <Link to={`/events${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
            Events
          </Link>
          <Link to={`/lectures/search${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
            Search Lectures
          </Link>
          
          {/* Community Features - Using centralized helper for consistent gating */}
          {isCommunityFeatureEnabled(config, 'announcements') && (
            <Link to={`/community/announcements${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Announcements
            </Link>
          )}
          {isCommunityFeatureEnabled(config, 'calendar') && (
            <Link to={`/community/calendar${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Calendar
            </Link>
          )}
          {isCommunityFeatureEnabled(config, 'qa') && (
            <Link to={`/community/qa${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Q&A
            </Link>
          )}
          {isCommunityFeatureEnabled(config, 'services') && (
            <Link to={`/community/services${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Community Services
            </Link>
          )}
          {isCommunityFeatureEnabled(config, 'tax_docs') && (
            <Link to={`/community/tax-docs${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Tax Documents
            </Link>
          )}
          {/* Embedded external apps (Library, Housing, ...). These were
              previously reachable only from the Services submenu in the bottom
              nav, which left them invisible on every screen that uses this menu
              instead. Registry-driven — see config/embeddedApps.js. */}
          {getEnabledEmbeddedApps(config).map(app => (
            <Link key={app.id} to={`${app.path}${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              {app.label}
            </Link>
          ))}
          
          {config?.publications_sheet_id && (
            <Link to={`/publications${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Publications
            </Link>
          )}
          {config?.about_us_enabled && (
            <Link to={`/about${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              About
            </Link>
          )}
          {config?.support_enabled && (
            <Link to={`/support${location.search}`} style={{ ...styles.navItem, textDecoration: 'none', color: 'inherit' }} onClick={toggleMenu}>
              Support
            </Link>
          )}

          {/* Conditional lecture navigation */}
          {isLecturePage && currentEventName && (
            <>
              {/* Show lecture navigation only when on a lecture page */}
              {!activeSection && !selectedSpeaker && (
                <>
                  <div 
                    style={styles.sectionHeader}
                    onClick={() => handleSectionClick('lectures')}
                  >
                    Lectures in {currentEventName}
                  </div>
                  
                  <div 
                    style={styles.sectionHeader}
                    onClick={() => handleSectionClick('speakers')}
                  >
                    Speakers in {currentEventName}
                  </div>
                </>
              )}

              {/* Lectures section */}
              {activeSection === 'lectures' && !selectedSpeaker && (
                <>
                  <div 
                    style={styles.backButton}
                    onClick={() => setActiveSection(null)}
                  >
                    ← Back to Menu
                  </div>
                  <div style={{ ...styles.sectionHeader, cursor: 'default' }}>
                    All Lectures in {currentEventName}
                  </div>
                  {lectureList}
                </>
              )}

              {/* Speakers section */}
              {activeSection === 'speakers' && !selectedSpeaker && (
                <>
                  <div 
                    style={styles.backButton}
                    onClick={() => setActiveSection(null)}
                  >
                    ← Back to Menu
                  </div>
                  <div style={{ ...styles.sectionHeader, cursor: 'default' }}>
                    All Speakers in {currentEventName}
                  </div>
                  {speakersList}
                </>
              )}

              {/* Speaker's lectures */}
              {selectedSpeaker && (
                <>
                  <div 
                    style={styles.backButton}
                    onClick={() => setSelectedSpeaker(null)}
                  >
                    ← Back to Speakers
                  </div>
                  <div style={{ ...styles.sectionHeader, cursor: 'default' }}>
                    Lectures by {selectedSpeaker}
                  </div>
                  {speakerLecturesList}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileHamburgerMenu;
